import { format, isAfter, isBefore, isValid, parse, parseISO } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Field } from '../Field/index.js';
import { Input } from '../Input/index.js';
import { MAX_DATE, MIN_DATE } from './constants.js';
const dateFormats = [
    'PPPP',
    'PPP',
    'PP',
    'P',
    'PPPPp',
    'PPPPp',
    'PPPp',
    'PPp',
    'Pp',
    'p',
    'PPPPpp',
    'PPPpp',
    'PPpp',
    'Ppp',
    'pp'
];
const defaultDateParser = (dateString, context) => {
    const parsed = parseISO(dateString);
    if (isValid(parsed)) {
        return parsed;
    }
    for (let i = 0; i < dateFormats.length; i++) {
        const parsed = parse(dateString, dateFormats[i], context);
        if (isValid(parsed)) {
            return parsed;
        }
    }
    return 'Unable to parse date';
};
/**
 * An input for dates.
 *
 * Attempts to parse the date when the input blurs or the user presses enter. If
 * successful, it will invoke `onSelectDate` with the parsed date. Otherwise, it
 * displays an error message.
 *
 * The default parser supports ISO formats and a variety of localized formats.
 */
export function DateInput({ dateParser = defaultDateParser, formatPattern = 'PPPPp', onSelectDate, max = MAX_DATE, min = MIN_DATE, selectedDate, ...fieldProps }) {
    const initialDateString = selectedDate ? format(selectedDate, formatPattern) : '';
    const [dateString, setDateString] = useState(initialDateString);
    const [errorMessage, setErrorMessage] = useState('');
    const intl = useIntl();
    useEffect(() => {
        if (selectedDate) {
            setDateString(format(selectedDate, formatPattern));
        }
        else {
            setDateString('');
        }
        setErrorMessage('');
    }, [selectedDate]);
    function handleDateChange(e) {
        setDateString(e.currentTarget.value);
    }
    function handleSelectDate(dateString) {
        if (!dateString)
            return;
        if (selectedDate) {
            // If the current date string is unchanged, skip invoking `onSelectDate`
            if (format(selectedDate, formatPattern) === dateString) {
                return;
            }
        }
        const context = selectedDate || new Date();
        const result = dateParser(dateString, context);
        if (typeof result === 'string') {
            setErrorMessage(result);
        }
        else if (isAfter(result, max)) {
            setErrorMessage(intl.formatMessage({
                defaultMessage: '{date, date, ::MMddyyyy} {date, time, short} is after the maximum, {max, date, ::MMddyyyy} {max, time, short}',
                id: 'iKATsM',
                description: 'Date input is after the maximum allowed'
            }, { date: result, max: max }));
        }
        else if (isBefore(result, min)) {
            setErrorMessage(intl.formatMessage({
                defaultMessage: '{date, date, ::MMddyyyy} {date, time, short} is before the minimum, {min, date, ::MMddyyyy} {min, time, short}',
                id: 'ZpfFQW',
                description: 'Date input is before the minimum allowed'
            }, { date: result, min: min }));
        }
        else {
            onSelectDate(result);
            setErrorMessage('');
        }
    }
    function handleBlur(e) {
        handleSelectDate(e.currentTarget.value);
    }
    function handleKeyDown(e) {
        if (e.key === 'Enter') {
            handleSelectDate(e.currentTarget.value);
        }
    }
    return (React.createElement(Field, { ...fieldProps, control: React.createElement(Input, { danger: !!errorMessage, onBlur: handleBlur, onChange: handleDateChange, onKeyDown: handleKeyDown, placeholder: format(new Date(), formatPattern), value: dateString }), error: errorMessage }));
}
//# sourceMappingURL=DateInput.js.map